import { API_URL } from "env";

import { mixLogOut } from '../services/mixin';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
const CACHE_KEY_PROVINCES = "loc_provinces";
const CACHE_KEY_WARDS_PREFIX = "loc_wards_";

export interface ProvinceItem {
    code: string;
    name: string;
    full_name: string;
    code_name: string;
    [key: string]: any;
}

export interface WardItem {
    id: number;
    name: string;
    code?: string;
    prefix?: string;
    province_code?: string;
    [key: string]: any;
}

interface CacheEntry<T> {
    data: T;
    timestamp: number;
}

function getCache<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const entry: CacheEntry<T> = JSON.parse(raw);
        if (Date.now() - entry.timestamp > CACHE_TTL) {
            localStorage.removeItem(key);
            return null;
        }
        return entry.data;
    } catch {
        return null;
    }
}

function setCache<T>(key: string, data: T): void {
    try {
        const entry: CacheEntry<T> = { data, timestamp: Date.now() };
        localStorage.setItem(key, JSON.stringify(entry));
    } catch {
        // localStorage full or unavailable — silently ignore
    }
}

async function getProvinces(): Promise<ProvinceItem[]> {
    const cached = getCache<ProvinceItem[]>(CACHE_KEY_PROVINCES);
    if (cached) return cached;

    const response = await fetch(`${API_URL}/api/v1/be/provinces`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
    });
    if (!response.ok) {
        if (response.status === 401) { mixLogOut(); }
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (!data.status) {
        throw new Error('Failed to fetch provinces');
    }
    const items: ProvinceItem[] = data.data?.items || data.data || [];
    setCache(CACHE_KEY_PROVINCES, items);
    return items;
}

async function getWardsByProvince(provinceCode: string): Promise<WardItem[]> {
    const cacheKey = `${CACHE_KEY_WARDS_PREFIX}${provinceCode}`;
    const cached = getCache<WardItem[]>(cacheKey);
    if (cached) return cached;

    const response = await fetch(`${API_URL}/api/v1/be/wards?province_code=${provinceCode}`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
    });
    if (!response.ok) {
        if (response.status === 401) { mixLogOut(); }
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (!data.status) {
        throw new Error('Failed to fetch wards');
    }
    const items: WardItem[] = data.data?.items || data.data || [];
    setCache(cacheKey, items);
    return items;
}

/** Clear all location caches (call when admin updates location data) */
function clearLocationCache(): void {
    localStorage.removeItem(CACHE_KEY_PROVINCES);
    const keys = Object.keys(localStorage);
    for (const key of keys) {
        if (key.startsWith(CACHE_KEY_WARDS_PREFIX)) {
            localStorage.removeItem(key);
        }
    }
}

export { getProvinces, getWardsByProvince, clearLocationCache };